import { TooltipDirectionType } from 'components/tooltip/Tooltip'

type DecideDirectionParams = {
  rect?: DOMRect | null
  margin?: number
  defaultDirection?: TooltipDirectionType
  /** true면 기본 방향을 강제로 유지 (오버플로우 상관없이) */
  lockDirection?: boolean
}

type DirectionContext = {
  hasTop: boolean
  hasBottom: boolean
  hasLeft: boolean
  hasRight: boolean
}

export const useTooltipDirectionSetting = () => {
  /**
   * @description 방향이 현재 뷰포트에서 들어갈 수 있는지 검사
   * * 뷰포트에 공간이 넉넉하다면 true를 반환, 그렇지 않다면 false를 반환
   * * true일 경우 defaultDirection을 유지, false일 경우 자동으로 방향을 결정
   * @param {TooltipDirectionType} dir - 툴팁 방향
   * @param {DirectionContext} ctx - 방향 컨텍스트
   * @returns {boolean} 방향이 현재 뷰포트에서 들어갈 수 있는지 여부
   */
  const fitsDirection = (dir: TooltipDirectionType, ctx: DirectionContext): boolean => {
    const { hasTop, hasBottom, hasLeft, hasRight } = ctx

    switch (dir) {
      case 'top':
        return hasBottom
      case 'bottom':
        return hasTop
      case 'left':
        return hasRight
      case 'right':
        return hasLeft
      case 'top-left':
        return hasBottom && hasRight
      case 'top-right':
        return hasBottom && hasLeft
      case 'bottom-left':
        return hasTop && hasLeft
      case 'bottom-right':
        return hasTop && hasRight
      case 'left-top':
        return hasLeft && hasTop
      case 'left-bottom':
        return hasLeft && hasBottom
      case 'right-top':
        return hasRight && hasTop
      case 'right-bottom':
        return hasRight && hasBottom
      default:
        return true
    }
  }

  /**
   *
   * @description 툴팁 방향 자동 결정 함수
   * @returns {TooltipDirectionType} 툴팁 방향
   */
  const decideTooltipDirection = ({
    rect,
    margin = 8,
    defaultDirection = 'top',
  }: DecideDirectionParams): TooltipDirectionType => {
    if (!rect) return defaultDirection

    const vw = window.innerWidth
    const vh = window.innerHeight

    // TODO: 임시로 버튼 크기만큼의 사이즈만 체크, 추후 tooltip size 추가 필요
    const tooltipSize = {
      width: rect.width,
      height: rect.height,
    }

    const hasBottom = vh - rect.bottom >= tooltipSize.height + margin
    const hasTop = rect.top >= tooltipSize.height + margin

    const hasRight = vw - rect.right >= tooltipSize.width + margin
    const hasLeft = rect.left >= tooltipSize.width + margin

    // 디폴트로 설정한 방향이 현재 화면에서 들어갈 수 있는지 검사
    const isFitsDirection = fitsDirection(defaultDirection, {
      hasTop,
      hasBottom,
      hasLeft,
      hasRight,
    })

    // 툴팁이 자리잡을 공간이 있다면 디폴트 방향을 반환
    if (isFitsDirection) return defaultDirection

    // 1) 오른쪽/왼쪽 공간은 충분하지만 아래에 공간이 없음 → bottom
    if (!hasBottom && hasLeft && hasRight) return 'bottom'

    // 2) 오른쪽 없음 + 아래 있음 → top-right
    if (hasBottom && !hasRight && hasLeft) return 'top-right'

    // 3) 왼쪽 없음 + 아래 있음 → top-left
    if (hasBottom && !hasLeft && hasRight) return 'top-left'

    // 4) 오른쪽·아래 둘 다 없음 → bottom-right
    if (!hasBottom && !hasRight && hasLeft) return 'bottom-right'

    // 5) 왼쪽·아래 둘 다 없음 → bottom-left
    if (!hasBottom && !hasLeft && hasRight) return 'bottom-left'

    // 나머지는 기본 규칙: 위에 못 뜨면 아래, 아래도 못 뜨면 그냥 기본값
    if (!hasTop && hasBottom) return 'bottom'
    if (!hasBottom && hasTop) return 'top'

    return defaultDirection
  }

  return {
    decideTooltipDirection,
  }
}
